import { PlanDay, PlanFrame, PlanKind } from '@/lib/contentPlanner';

// ---- helpers ----

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadText(filename: string, text: string, mime = 'text/plain') {
  downloadBlob(filename, new Blob([text], { type: `${mime};charset=utf-8` }));
}

function csvEscape(v: string): string {
  const s = (v ?? '').toString();
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// ---- FEED EXPORTS ----

/**
 * Plain-text caption sheet — one block per post, ready to copy/paste straight into Instagram.
 */
export function exportFeedCaptionsTxt(days: PlanDay[]): void {
  const lines: string[] = [];
  lines.push('ZERA TRAVEL — INSTAGRAM FEED CAPTIONS');
  lines.push('Generated ' + new Date().toLocaleString());
  lines.push('='.repeat(60));
  lines.push('');

  days.forEach((day, dIdx) => {
    day.frames.forEach((f, fIdx) => {
      lines.push(`── ${day.day_label.toUpperCase()} · ${day.date_label} ──`);
      lines.push(`Theme:    ${day.theme}`);
      lines.push(`Format:   ${f.post_type}`);
      lines.push(`Time:     ${f.post_time}`);
      lines.push(`Status:   ${f.approval_status}`);
      lines.push(`Image:    ${f.image || '(none)'}`);
      lines.push('');
      lines.push('CAPTION:');
      lines.push('-'.repeat(60));
      lines.push(f.caption || '(no caption)');
      lines.push('');
      if (f.cta) {
        lines.push('CTA:');
        lines.push(f.cta);
        lines.push('');
      }
      if (f.hashtags.length) {
        lines.push('HASHTAGS:');
        lines.push(f.hashtags.join(' '));
        lines.push('');
      }
      lines.push('READY-TO-POST BLOCK (caption + CTA + hashtags):');
      lines.push('-'.repeat(60));
      const block = [f.caption, f.cta, '', f.hashtags.join(' ')].filter(Boolean).join('\n\n');
      lines.push(block);
      lines.push('');
      lines.push('='.repeat(60));
      lines.push('');
    });
  });

  downloadText('zera-feed-captions.txt', lines.join('\n'));
}

/**
 * CSV compatible with Later / Hootsuite / Buffer bulk import:
 * date, time, caption, hashtags, media_url, post_type, status
 */
export function exportFeedSchedulerCsv(days: PlanDay[]): void {
  const header = [
    'day',
    'date_label',
    'theme',
    'post_time',
    'post_type',
    'approval_status',
    'caption',
    'cta',
    'hashtags',
    'media_url',
  ];
  const rows: string[][] = [header];

  days.forEach((day) => {
    day.frames.forEach((f) => {
      const fullCaption = [f.caption, f.cta].filter(Boolean).join('\n\n');
      rows.push([
        day.day_label,
        day.date_label,
        day.theme,
        f.post_time,
        f.post_type,
        f.approval_status,
        fullCaption,
        f.cta,
        f.hashtags.join(' '),
        f.image,
      ]);
    });
  });

  const csv = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  downloadText('zera-feed-schedule.csv', csv, 'text/csv');
}

/**
 * Hashtag set library — grouped by theme/day, ready to save into Instagram's saved replies.
 */
export function exportHashtagSetsTxt(days: PlanDay[]): void {
  const lines: string[] = [];
  lines.push('ZERA TRAVEL — HASHTAG SET LIBRARY');
  lines.push('Generated ' + new Date().toLocaleString());
  lines.push('Tip: save each block as an Instagram "Saved Reply" for one-tap posting.');
  lines.push('='.repeat(60));
  lines.push('');

  const allTags = new Set<string>();

  days.forEach((day) => {
    day.frames.forEach((f) => {
      if (!f.hashtags.length) return;
      lines.push(`SET: ${day.day_label} — ${day.theme}`);
      lines.push('-'.repeat(60));
      lines.push(f.hashtags.join(' '));
      lines.push('');
      lines.push(`Count: ${f.hashtags.length}/30`);
      lines.push('');
      f.hashtags.forEach((t) => allTags.add(t.toLowerCase()));
    });
  });

  lines.push('='.repeat(60));
  lines.push('MASTER LIBRARY (all unique tags, alphabetical):');
  lines.push('-'.repeat(60));
  lines.push(Array.from(allTags).sort().join(' '));

  downloadText('zera-hashtag-sets.txt', lines.join('\n'));
}

// ---- STORIES EXPORTS ----

/**
 * Story shooting script — one document the content producer takes on set.
 */
export function exportStoryScriptTxt(days: PlanDay[]): void {
  const lines: string[] = [];
  lines.push('ZERA TRAVEL — INSTAGRAM STORY SHOOTING SCRIPT');
  lines.push('Generated ' + new Date().toLocaleString());
  lines.push('Format: 1080×1920 (9:16), .mp4 or .jpg');
  lines.push('='.repeat(60));
  lines.push('');

  days.forEach((day) => {
    lines.push('');
    lines.push(`▌ ${day.day_label.toUpperCase()} · ${day.date_label}`);
    lines.push(`▌ Theme: ${day.theme}`);
    lines.push(`▌ Status: ${day.approval_status}`);
    if (day.narrative) {
      lines.push('');
      lines.push('NARRATIVE:');
      lines.push(day.narrative);
    }
    lines.push('');
    lines.push(`${day.frames.length} frames`);
    lines.push('─'.repeat(60));

    day.frames.forEach((f, idx) => {
      lines.push('');
      lines.push(`  Frame ${idx + 1} of ${day.frames.length}  [${f.approval_status}]`);
      lines.push(`  Headline:    ${f.headline}`);
      lines.push(`  On-screen:   ${f.copy.replace(/\n/g, ' / ')}`);
      lines.push(`  Image:       ${f.image || '(none)'}`);
      if (f.link_destination) lines.push(`  Link → :     ${f.link_destination}`);
      if (f.stickers.length) {
        lines.push(`  Stickers:`);
        f.stickers.forEach((s) => {
          lines.push(`    • [${s.type}] ${s.label}${s.detail ? ` — ${s.detail}` : ''}`);
        });
      }
    });
    lines.push('');
  });

  downloadText('zera-story-script.txt', lines.join('\n'));
}

/**
 * Stories CSV — one row per frame, easy to hand a designer for batch graphic creation.
 */
export function exportStoryCsv(days: PlanDay[]): void {
  const header = [
    'day',
    'date_label',
    'theme',
    'frame_number',
    'approval_status',
    'headline',
    'on_screen_copy',
    'stickers',
    'link_destination',
    'image_url',
  ];
  const rows: string[][] = [header];
  days.forEach((day) => {
    day.frames.forEach((f, idx) => {
      const stickerStr = f.stickers
        .map((s) => `[${s.type}] ${s.label}${s.detail ? ` (${s.detail})` : ''}`)
        .join(' | ');
      rows.push([
        day.day_label,
        day.date_label,
        day.theme,
        String(idx + 1),
        f.approval_status,
        f.headline,
        f.copy,
        stickerStr,
        f.link_destination,
        f.image,
      ]);
    });
  });
  const csv = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  downloadText('zera-story-schedule.csv', csv, 'text/csv');
}

// ---- SHARED ----

/**
 * Image asset manifest — list of every image URL so the social manager can bulk-download.
 * Includes both a plain text list AND an HTML page where every link is clickable + previewable.
 */
export function exportImageManifest(days: PlanDay[], kind: PlanKind): void {
  const urls: { day: string; idx: number; url: string }[] = [];
  days.forEach((d) => {
    d.frames.forEach((f, i) => {
      if (f.image) urls.push({ day: `${d.day_label} · ${d.date_label}`, idx: i + 1, url: f.image });
    });
  });

  const html = `<!doctype html>
<html><head><meta charset="utf-8" /><title>Zera ${kind} — Image Assets</title>
<style>
  body { font-family: -apple-system, system-ui, sans-serif; background: #f8fafc; margin: 0; padding: 40px; color: #0f172a; }
  h1 { font-weight: 300; letter-spacing: 1px; }
  .meta { color: #64748b; font-size: 13px; margin-bottom: 30px; }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 20px; }
  .card { background: white; border: 1px solid #e2e8f0; padding: 12px; }
  .card img { width: 100%; aspect-ratio: ${kind === 'feed' ? '1 / 1' : '9 / 16'}; object-fit: cover; display: block; }
  .card .day { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: #8b6f47; margin-top: 10px; }
  .card .url { font-size: 11px; color: #64748b; word-break: break-all; margin-top: 4px; }
  .card a { display: inline-block; margin-top: 8px; padding: 6px 10px; background: #1A3B5C; color: white; text-decoration: none; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; }
  .toolbar { margin-bottom: 20px; padding: 16px; background: #1A3B5C; color: white; }
  .toolbar code { background: rgba(255,255,255,0.1); padding: 2px 6px; }
</style></head>
<body>
  <h1>Zera Travel — ${kind === 'feed' ? 'Feed Post' : 'Story'} Image Assets</h1>
  <div class="meta">${urls.length} images · Generated ${new Date().toLocaleString()}</div>
  <div class="toolbar">
    <strong>Bulk download tip:</strong> Right-click anywhere on this page → "Save Page As… → Web Page, Complete" to grab every image at once.
    Or use a browser extension like <em>Image Downloader</em> / <em>DownThemAll!</em>.
  </div>
  <div class="grid">
    ${urls
      .map(
        (u) => `
      <div class="card">
        <img src="${u.url}" alt="" loading="lazy" />
        <div class="day">${u.day} · Frame ${u.idx}</div>
        <div class="url">${u.url}</div>
        <a href="${u.url}" download target="_blank" rel="noopener">Open / Save</a>
      </div>`,
      )
      .join('')}
  </div>
</body></html>`;

  downloadText(`zera-${kind}-images.html`, html, 'text/html');
}

/**
 * Full JSON backup — full state of the plan, useful for archiving or migrating.
 */
export function exportJsonBackup(days: PlanDay[], kind: PlanKind): void {
  const payload = {
    exported_at: new Date().toISOString(),
    kind,
    days,
  };
  downloadText(`zera-${kind}-backup.json`, JSON.stringify(payload, null, 2), 'application/json');
}

/**
 * Press-kit / printable weekly plan — beautifully styled HTML that prints to PDF cleanly.
 */
export function exportPrintablePlan(days: PlanDay[], kind: PlanKind): void {
  const isFeed = kind === 'feed';
  const title = isFeed ? 'Instagram Feed — Weekly Plan' : 'Instagram Stories — Weekly Plan';

  const renderFrame = (f: PlanFrame, idx: number) => {
    if (isFeed) {
      return `
        <div class="frame">
          <div class="frame-img" style="aspect-ratio: 1 / 1; background-image: url('${f.image}')"></div>
          <div class="frame-body">
            <div class="status status-${f.approval_status.toLowerCase()}">${f.approval_status}</div>
            <div class="meta">${f.post_type} · ${f.post_time}</div>
            <div class="caption">${(f.caption || '').replace(/\n/g, '<br/>')}</div>
            ${f.cta ? `<div class="cta">${f.cta}</div>` : ''}
            ${f.hashtags.length ? `<div class="tags">${f.hashtags.join(' ')}</div>` : ''}
          </div>
        </div>`;
    }
    return `
      <div class="frame story">
        <div class="frame-img" style="aspect-ratio: 9 / 16; background-image: url('${f.image}')">
          <div class="story-overlay">
            <div class="story-headline">${f.headline}</div>
            <div class="story-copy">${(f.copy || '').replace(/\n/g, '<br/>')}</div>
          </div>
        </div>
        <div class="frame-body">
          <div class="status status-${f.approval_status.toLowerCase()}">${f.approval_status} · Frame ${idx + 1}</div>
          ${
            f.stickers.length
              ? `<ul class="stickers">${f.stickers
                  .map((s) => `<li><strong>${s.type}:</strong> ${s.label}${s.detail ? ` — ${s.detail}` : ''}</li>`)
                  .join('')}</ul>`
              : ''
          }
          ${f.link_destination ? `<div class="link">→ ${f.link_destination}</div>` : ''}
        </div>
      </div>`;
  };

  const html = `<!doctype html>
<html><head><meta charset="utf-8" /><title>Zera — ${title}</title>
<style>
  @page { size: A4; margin: 18mm; }
  body { font-family: Georgia, serif; color: #0f172a; margin: 0; padding: 30px; background: #f8fafc; }
  h1 { font-weight: 300; font-size: 28px; letter-spacing: 1px; margin: 0; }
  .sub { color: #8b6f47; text-transform: uppercase; letter-spacing: 3px; font-size: 11px; font-family: sans-serif; margin-top: 8px; }
  .day { background: white; border: 1px solid #e2e8f0; padding: 24px; margin: 24px 0; page-break-inside: avoid; }
  .day-head { display: flex; align-items: baseline; gap: 16px; border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 16px; }
  .day-head h2 { margin: 0; font-weight: 300; font-size: 22px; }
  .day-head .label { color: #8b6f47; text-transform: uppercase; letter-spacing: 2px; font-size: 10px; font-family: sans-serif; }
  .day-head .theme { color: #64748b; font-style: italic; }
  .narrative { font-style: italic; color: #475569; font-size: 13px; margin-bottom: 16px; border-left: 3px solid #C9A961; padding-left: 12px; }
  .frames { display: grid; grid-template-columns: ${isFeed ? 'repeat(auto-fit, minmax(280px, 1fr))' : 'repeat(auto-fit, minmax(220px, 1fr))'}; gap: 16px; }
  .frame { border: 1px solid #e2e8f0; }
  .frame-img { background-size: cover; background-position: center; background-color: #f1f5f9; }
  .frame.story .frame-img { position: relative; }
  .story-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.6) 100%); display: flex; flex-direction: column; justify-content: flex-end; padding: 12px; color: white; font-family: sans-serif; }
  .story-headline { font-size: 14px; letter-spacing: 1px; text-transform: lowercase; font-weight: 600; }
  .story-copy { font-size: 11px; margin-top: 6px; line-height: 1.4; }
  .frame-body { padding: 12px; font-family: sans-serif; font-size: 12px; }
  .meta { color: #64748b; font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 8px; }
  .caption { white-space: pre-wrap; line-height: 1.5; font-family: Georgia, serif; font-size: 13px; }
  .cta { margin-top: 8px; color: #1A3B5C; font-weight: 600; font-size: 12px; }
  .tags { margin-top: 8px; color: #64748b; font-size: 11px; word-break: break-word; }
  .status { display: inline-block; padding: 2px 8px; border-radius: 0; font-size: 9px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
  .status-draft { background: #f1f5f9; color: #475569; }
  .status-approved { background: #d1fae5; color: #065f46; }
  .status-scheduled { background: #fef3c7; color: #92400e; }
  .stickers { list-style: none; padding: 0; margin: 8px 0 0; font-size: 11px; color: #475569; }
  .stickers li { padding: 2px 0; }
  .link { margin-top: 8px; color: #1A3B5C; font-size: 11px; }
  @media print { body { background: white; padding: 0; } .day { border: none; padding: 0; } }
</style></head>
<body>
  <h1>Zera Travel — ${title}</h1>
  <div class="sub">${days.length} days · Generated ${new Date().toLocaleString()}</div>
  ${days
    .map(
      (d) => `
    <div class="day">
      <div class="day-head">
        <span class="label">${d.date_label}</span>
        <h2>${d.day_label}</h2>
        <span class="theme">${d.theme}</span>
      </div>
      ${d.narrative ? `<div class="narrative">${d.narrative}</div>` : ''}
      <div class="frames">
        ${d.frames.map(renderFrame).join('')}
      </div>
    </div>`,
    )
    .join('')}
  <script>setTimeout(() => window.print(), 600);</script>
</body></html>`;

  // Open in new tab so the user can print/save-as-PDF, AND download the file
  const win = window.open('', '_blank');
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
  } else {
    downloadText(`zera-${kind}-plan.html`, html, 'text/html');
  }
}
